import React, {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Button, Modal} from "react-bootstrap";
import axios from "axios";
import {API_URL} from "../../../../config";
import {createIdempotencyKey} from "./rsoSalesForecastHelpers";
import {denverMonthDay, monthDate, monthKey, monthName, overrideCodeOf, overrideRefusalMessage, signOffBody, validSignOff,
  windowCodeOf} from "./rsoForecastWindowHelpers";
import RsoOverrideSignOff from "./RsoOverrideSignOff";

const endpoint = `${API_URL}/Dashboard/api/RsoPropertyForecast`;
const plural = (value, one, many) => `${Number(value).toLocaleString()} ${Number(value) === 1 ? one : many}`;
const listWords = (items) => items.length <= 1 ? items : [...items.slice(0, -1).flatMap((item, index) =>
  index ? [", ", item] : [item]), " and ", items[items.length - 1]];

// Plain words for the refusals Sales can fix themselves.
export const noUnitsRefusal = (failure, month) => {
  const code = failure?.response?.data?.code;
  const message = typeof failure?.response?.data?.message === "string" ? failure.response.data.message : "";
  if (overrideCodeOf(failure)) return overrideRefusalMessage(code, month, message);
  if (code === "material_change_sent") return `Withdraw the ${monthName(month)} material change first.`;
  if (code === "material_change_unsent") return "Send or discard the material change first.";
  if (code === "no_units_unavailable") return "No units this month isn't available right now.";
  if (windowCodeOf(failure)) return message || `${monthName(month)} can't be changed now.`;
  if (failure?.response?.status === 409) return "This property changed since you opened it. Check it again.";
  if (failure?.response?.status === 401) return "Sign in again to continue.";
  return message || "Could not check this property. Try again.";
};

const validReview = (data, contactInfoId, month, expectedRevision) => data && Number(data.contactInfoId) === Number(contactInfoId)
  && monthKey(data.forecastMonth) === monthKey(month) && typeof data.reviewToken === "string" && data.reviewToken
  && Number(data.expectedRevision) === Number(expectedRevision) && Array.isArray(data.replaces);
const validReceipt = (data, expectedRevision) => data?.noUnits === true && Number.isSafeInteger(data.revision)
  && data.revision > Number(expectedRevision) && Array.isArray(data.entries)
  && data.entries.every((entry) => Number(entry.unitsTurning) === 0 && Number(entry.quantity) === 0);
// Override refusals Sales can fix in the sign-off. Nothing was saved, so the corrected sign-off goes as a new request.
const FIXABLE_OVERRIDE_CODES = ["override_signoff_invalid", "override_signoff_required"];

// Confirms "No units this month" for one property and month, then sends it to Purchasing.
// onStale: the property changed (409). The page reads it again and reopens this confirmation on the new revision,
// so a review is never repeated against the stale one.
// override: {caseLine, today, signOff, onSignOffChange} while Sales sends this month outside the input window.
// It is read when the review starts and frozen on the review, so the review and its send always agree (the page opens a
// new confirmation when an override starts or ends). onSent(receipt, asOverride); onOverrideRefusal(code, message).
// onOverrideSendPending(pending): true while an override send is in flight or awaits its identical retry, so the page keeps
// the override (and this confirmation) until it is answered, even if the month opens meanwhile.
export default function RsoNoUnitsDialog({token, contactInfoId, propertyName, month, expectedRevision, onCancel, onSent, onWindowRefusal,
  onStale, notice = "", override = null, onOverrideRefusal, onOverrideSendPending}) {
  const [state, setState] = useState({status: "loading", review: null, message: ""});
  const requestId = useRef(null);
  const busy = useRef(false);
  const generation = useRef(0);
  const config = useCallback(() => ({headers: {Authorization: `Bearer ${token}`}, timeout: 30000}), [token]);
  // Callbacks from the page change on every render; reading them through a ref keeps the review from repeating.
  const windowRefusal = useRef(onWindowRefusal);
  windowRefusal.current = onWindowRefusal;
  const staleCallback = useRef(onStale);
  staleCallback.current = onStale;
  const overrideRef = useRef(override);
  overrideRef.current = override;
  const overrideRefusal = useRef(onOverrideRefusal);
  overrideRefusal.current = onOverrideRefusal;
  const overridePending = useRef(onOverrideSendPending);
  overridePending.current = onOverrideSendPending;
  // lockedSignOff: the sign-off of an override send in flight or awaiting its retry.
  const pending = !!state.lockedSignOff;
  useEffect(() => {overridePending.current?.(pending);}, [pending]);
  const refused = useCallback((failure) => {
    if (windowCodeOf(failure)) windowRefusal.current?.(windowCodeOf(failure), failure?.response?.data?.message);
    if (overrideCodeOf(failure)) overrideRefusal.current?.(overrideCodeOf(failure), failure?.response?.data?.message);
    return noUnitsRefusal(failure, month);
  }, [month]);
  const review = useCallback(async () => {
    const current = ++generation.current;
    const outsideWindow = !!overrideRef.current;
    setState({status: "loading", review: null, message: ""});
    requestId.current = null;
    try {
      const response = await axios.post(`${endpoint}/no-units/review`,
        {contactInfoId, forecastMonth: monthDate(month), expectedRevision, ...(outsideWindow ? {outsideWindow: true} : {})}, config());
      if (current !== generation.current) return;
      if (!validReview(response.data, contactInfoId, month, expectedRevision)) throw new Error("No units review was not confirmed");
      requestId.current = createIdempotencyKey();
      setState({status: "ready", review: outsideWindow ? {...response.data, outsideWindow: true} : response.data, message: ""});
    } catch (failure) {
      if (current !== generation.current) return;
      const stale = failure?.response?.status === 409;
      setState({status: "refused", review: null, message: refused(failure), stale});
      if (stale) staleCallback.current?.();
    }
  }, [config, contactInfoId, expectedRevision, month, refused]);
  useEffect(() => {review(); return () => {generation.current += 1;};}, [review]);
  const send = async () => {
    const reviewed = state.review;
    if (busy.current || !reviewed) return;
    // An override carries the sign-off; a retry after an unknown outcome repeats the one it sent.
    const signOff = reviewed.outsideWindow === true ? state.lockedSignOff || signOffBody(overrideRef.current?.signOff) : null;
    if (signOff && !validSignOff(signOff)) return;
    const current = generation.current;
    busy.current = true;
    // The sign-off is locked from now until the send is answered.
    setState((previous) => ({...previous, status: "sending", message: "", lockedSignOff: signOff}));
    try {
      const response = await axios.post(`${endpoint}/no-units`, {contactInfoId, forecastMonth: monthDate(month),
        expectedRevision: reviewed.expectedRevision, reviewToken: reviewed.reviewToken, requestId: requestId.current,
        ...(signOff ? {outsideWindow: true, signOff} : {})}, config());
      if (!validReceipt(response.data, reviewed.expectedRevision)) throw new Error("No units was not confirmed");
      onSent(response.data, !!signOff);
    } catch (failure) {
      const confirmed = !!failure?.response;
      // A refusal (4xx) needs Sales to act first; a lost or failed response can be retried with the same request ID.
      const stale = failure?.response?.status === 409;
      const refusal = confirmed && failure.response.status < 500;
      const fixable = refusal && FIXABLE_OVERRIDE_CODES.includes(overrideCodeOf(failure));
      if (fixable) requestId.current = createIdempotencyKey();
      const message = confirmed ? refused(failure) : "Could not confirm whether No units was sent. Check this property before trying again.";
      // The page may have replaced this confirmation meanwhile (an override that ended opens a normal one).
      if (current !== generation.current) return;
      setState((previous) => ({...previous, status: refusal && !fixable ? "refused" : "ready", stale, message,
        // While the outcome is unknown the sign-off stays locked, so trying again sends exactly the same request.
        lockedSignOff: refusal ? null : signOff}));
      if (stale) staleCallback.current?.();
    } finally {busy.current = false;}
  };
  const replaces = state.review?.replaces?.filter((row) => Number(row.unitsTurning) > 0) || [];
  const sentOn = denverMonthDay(state.review?.lastSentAt);
  const sending = state.status === "sending";
  const signOff = state.lockedSignOff || override?.signOff;
  const signOffMissing = state.review?.outsideWindow === true && !validSignOff(signOff);
  return <Modal show centered animation={false} className="rpf-material-change rpf-no-units"
    backdropClassName="rpf-material-change-backdrop" aria-labelledby="rpf-no-units-title"
    backdrop={sending ? "static" : true} keyboard={!sending} onHide={() => {if (!sending) onCancel();}}>
    <Modal.Header closeButton={!sending}><Modal.Title id="rpf-no-units-title">No units this month</Modal.Title></Modal.Header>
    <Modal.Body>
      <p><strong>{propertyName}</strong> won&apos;t need any RSO units in {monthName(month)}? Purchasing won&apos;t plan RSO material for it in {monthName(month)}.</p>
      {notice && <Alert variant="info">{notice}</Alert>}
      {state.status === "loading" && <p role="status">Checking this property…</p>}
      {!!replaces.length && <p>This replaces the {listWords(replaces.map((row, index) => <React.Fragment key={`${row.productId}-${index}`}>
        {plural(row.unitsTurning, "unit turn", "unit turns")} of <em>{row.productName || "a material"}</em></React.Fragment>))} you sent {sentOn || "earlier"}.</p>}
      {override && <RsoOverrideSignOff idPrefix="rpf-no-units-signoff" caseLine={override.caseLine} today={override.today}
        value={signOff} onChange={override.onSignOffChange} disabled={sending || !!state.lockedSignOff} />}
      {state.message && <Alert variant={state.status === "refused" ? "warning" : "danger"} role="alert">{state.message}</Alert>}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="outline-secondary" disabled={sending} onClick={onCancel}>Cancel</Button>
      {state.stale ? <Button disabled={sending} onClick={() => (staleCallback.current ? staleCallback.current() : review())}>Check again</Button>
        : <Button variant="success" disabled={!state.review || state.status === "refused" || sending || signOffMissing} onClick={send}>
          {sending ? "Sending…" : override ? "Send No units as override" : "Send No units this month"}</Button>}
    </Modal.Footer>
  </Modal>;
}
